import { useMemo, useState } from 'react';

import { Box, Skeleton } from '@mui/material';
import { ApexOptions } from 'apexcharts';
import { groupBy } from 'lodash';
import ReactApexChart from 'react-apexcharts';
import { useQuery } from 'react-query';
import { If } from 'src/components/If';
import { getProfitabilityDataCharts } from 'src/services/operation/operationFidc';
import { Profitability } from 'src/types/operations/fidc';

import { GroupButtonPeriod, Period } from '../../components/GroupButtonPeriod';
import {
  colors,
  fakeProfitabilityData,
  getLimitZoomMountedHandler,
  getPeriodDate,
  getQuotaKey,
  getQuotaLabel,
  locale,
  noData,
} from '../utils';

const defaultPeriodInDays = 180;

type SeriesAccumulator = Record<
  string,
  { name: string; values: Record<string, number> }
>;

type ProfitabilityChartsProps = {
  hashId: string;
  hasPermission: boolean;
};

export function ProfitabilityCharts({
  hashId,
  hasPermission,
}: ProfitabilityChartsProps) {
  const [selectedQuotas, setSelectedQuotas] = useState<string[]>([]);
  const [selectedPeriod, setSelectedPeriod] =
    useState<Period>(defaultPeriodInDays);

  const { data: rawData, isLoading } = useQuery({
    queryKey: ['profitability-charts', hashId, selectedPeriod],
    queryFn: () =>
      getProfitabilityDataCharts(hashId, getPeriodDate(selectedPeriod)),
    onSuccess: (data) => {
      const rawData = hasPermission ? data : fakeProfitabilityData;
      const groupedByQuotaSerie = groupBy(rawData, getQuotaKey);
      setSelectedQuotas(Object.keys(groupedByQuotaSerie));
    },
    enabled: !!hashId,
    refetchOnWindowFocus: false,
  });

  const data = hasPermission ? rawData : fakeProfitabilityData;

  const handlePeriodChange = (days: Period) => {
    setSelectedPeriod(days);
  };

  const chartData = useMemo(() => {
    if (!data || data.length === 0 || selectedQuotas.length === 0) {
      return { series: [] };
    }

    const filteredData = data.filter((item) =>
      selectedQuotas.includes(getQuotaKey(item)),
    );

    const allDates = [...new Set(filteredData.map((item) => item.data))].sort();

    const groupedByQuota = filteredData.reduce(
      (acc: SeriesAccumulator, item: Profitability) => {
        const key = getQuotaKey(item);
        if (!acc[key]) {
          acc[key] = {
            name: getQuotaLabel(item.cota, item.serie),
            values: {},
          };
        }
        acc[key].values[item.data] = item.rentabilidadeDiaria;
        return acc;
      },
      {} as SeriesAccumulator,
    );

    const series = Object.values(groupedByQuota).map(({ name, values }) => ({
      name,
      data: allDates.map((date) => {
        const timestamp = new Date(date).getTime();
        const value = values[date];
        return {
          x: timestamp,
          y: value || null,
        };
      }),
    }));

    return { series };
  }, [data, selectedQuotas]);

  const options: ApexOptions = {
    series: chartData.series,
    colors: colors,
    chart: {
      type: 'line',
      defaultLocale: 'pt-BR',
      locales: [locale],
      zoom: {
        type: 'xy',
        enabled: true,
        autoScaleYaxis: false,
      },
      toolbar: {
        autoSelected: 'zoom',
      },
      events: {
        updated: getLimitZoomMountedHandler(),
      },
    },
    stroke: {
      curve: 'smooth',
      width: 4,
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
      hover: {
        size: 6,
      },
    },
    legend: {
      show: true,
      showForSingleSeries: true,
      position: 'top',
      horizontalAlign: 'center',
      floating: false,
      offsetY: 20,
      height: 50,
      itemMargin: {
        horizontal: 10,
        vertical: 5,
      },
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          if (val === null || val === undefined) return '';
          return val.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
      },
      title: {
        text: 'Rentabilidade (%)',
        style: {
          fontFamily: 'Inter',
        },
      },
    },
    xaxis: {
      type: 'datetime',
    },
    tooltip: {
      shared: true,
      intersect: false,
      x: {
        format: 'dd/MM/yyyy',
      },
      y: {
        formatter: function (val) {
          if (val === null || val === undefined) return '';
          return (
            val.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 4,
            }) + '%'
          );
        },
      },
    },
    noData: noData,
  };
  return (
    <Box sx={{ '.apexcharts-menu-item.exportCSV': { display: 'none' } }}>
      <If condition={isLoading}>
        <Skeleton variant="rectangular" animation="wave" height={420} />
      </If>
      <If condition={!isLoading}>
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          gap={2}
          alignItems="flex-end">
          <GroupButtonPeriod
            selectedPeriod={selectedPeriod}
            handlePeriodChange={handlePeriodChange}
          />
        </Box>
        <ReactApexChart
          type="line"
          series={chartData.series}
          options={options}
          height={420}
        />
      </If>
    </Box>
  );
}
